import { Button, ButtonGroup, Card, Grid2, Typography } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { useContext, useState } from "react";
import { AppContext } from "../context/AppContext";
import ReactApexChart from "react-apexcharts";
import { formatChartData } from "../helpers/utils";
import { ApexOptions } from "apexcharts";
import { useMortgage } from "./mortgage/MortgageContext";

const MORTGAGE_COLOR = "#8b95a7";

enum ChartView {
	SIX_MONTHS = "6 Months",
	ONE_YEAR = "1 Year",
	MAX = "Max",
}

const unselectedColor = "#FFFFFF";
const selectedColor = "#f1f1f1";
export default function ValueTrendCard() {
	const { appState } = useContext(AppContext);
	const [selectedChartView, setSelectedChartView] = useState(ChartView.MAX);
	const { report_configuration, currentAddrId, info, disclaimer } = appState;
	var dataSource = report_configuration?.find((_) => _?.addrId === currentAddrId);
	const mortgageHistory = useMortgage()?.mortgage?.history;
	var seriesArea: Array<any> = GetSeriesArea();
	const withMortgage = seriesArea.length > 1;
	// The axis covers every value point, as before, plus the mortgage line when there is one.
	const yValues = (dataSource?.reportData.data ?? [])
		.map((_) => Number(_.y))
		.concat(withMortgage ? seriesArea[1].data.map((_: any) => Number(_.y)) : []);
	let minY = 0;
	let maxY = 0;
	if (yValues.length) {
		minY = Math.min(...yValues) * 0.9; //-10%
		maxY = Math.max(...yValues) * 1.05; // +5%
	}
	const lineColorScheme = appState.colorPallete["scheme4"];
	const areaColorScheme = appState.colorPallete["scheme5"];
	// Without a mortgage line these are exactly the options the chart always had.
	const optionArea: ApexOptions = {
		colors: withMortgage ? [lineColorScheme, MORTGAGE_COLOR] : [lineColorScheme],
		fill:
			info.agent.valuationChartType === "AREA"
				? withMortgage
					? {
							type: ["gradient", "solid"],
							colors: [areaColorScheme, MORTGAGE_COLOR],
							// A mixed chart shades left to right unless told otherwise; keep the usual fade down.
							gradient: {
								type: "vertical",
								shadeIntensity: 0,
								opacityFrom: 0.65,
								opacityTo: 0.15,
								stops: [0, 100],
							},
						}
					: { type: "gradient", colors: [areaColorScheme] }
				: { type: "solid", colors: withMortgage ? [areaColorScheme, MORTGAGE_COLOR] : [areaColorScheme] },
		tooltip: {
			// theme: skin === "dark" ? "dark" : "light",
			enabled: true,
			style: {
				fontSize: "16px",
				fontFamily: "inherit",
			},
			x: {
				show: true,
				format: "MMM yyyy",
			},
		},
		grid: {
			show: true,
			xaxis: {
				lines: {
					show: false,
				},
			},
			yaxis: {
				lines: {
					show: true,
				},
			},
		},
		chart: {
			type: "area",
			toolbar: {
				show: false,
			},
			zoom: {
				enabled: true,
			},
		},
		dataLabels: {
			enabled: false,
		},
		xaxis: {
			type: "datetime",
			labels: {
				datetimeUTC: true,
				format: "MMM yyyy",
			},
		},
		yaxis: {
			tickAmount: 5,
			axisTicks: {
				show: true,
				offsetX: 0,
				offsetY: 0,
			},
			min: minY,
			max: maxY,
			opposite: false,
			labels: {
				minWidth: 35,
				rotate: 0,
				style: {
					fontSize: "10px",
				},
				formatter: function (value: number) {
					return `${formatChartData(value)}`;
				},
			},
		},
		legend: {
			horizontalAlign: "left",
		},
		stroke: withMortgage
			? {
					curve: "smooth",
					width: [info.agent.valuationChartType === "BAR" ? 0 : 3, 2],
					dashArray: [0, 5],
				}
			: { curve: "smooth" },
	};
	function GetSeriesArea() {
		if (report_configuration && currentAddrId && dataSource) {
			let filteredData = [];
			let cutOffDateStart: Date;
			let cutOffDateEnd: Date;
			if (selectedChartView === ChartView.SIX_MONTHS) {
				cutOffDateStart = new Date();
				cutOffDateStart.setMonth(cutOffDateStart.getMonth() - 6);
				cutOffDateStart.setDate(1);
				cutOffDateStart.setHours(0, 0, 0, 0);
				cutOffDateEnd = new Date();
				cutOffDateEnd.setDate(0);
				cutOffDateEnd.setHours(23, 59, 59, 999);
			} else if (selectedChartView === ChartView.ONE_YEAR) {
				cutOffDateStart = new Date();
				cutOffDateStart.setFullYear(cutOffDateStart.getFullYear() - 1);
				cutOffDateStart.setMonth(0);
				cutOffDateStart.setDate(1);
				cutOffDateStart.setHours(0, 0, 0, 0);
				cutOffDateEnd = new Date();
			} else if (selectedChartView === ChartView.MAX) {
				cutOffDateStart = new Date(dataSource.reportData.data[0].x);
				cutOffDateEnd = new Date();
			}

			filteredData = dataSource.reportData.data.filter((_) => {
				const refDate = new Date(_.x);
				refDate.setMinutes(refDate.getMinutes() + refDate.getTimezoneOffset());
				var match = refDate >= cutOffDateStart && refDate <= cutOffDateEnd;
				return match;
			});
			const series: Array<any> = [{ name: "Value Estimate", data: filteredData }];
			// Mortgage Insights: the homeowner's estimated mortgage balance over the same months.
			if (filteredData.length >= 2 && mortgageHistory?.length) {
				const first = new Date(filteredData[0].x).getTime();
				const last = new Date(filteredData[filteredData.length - 1].x).getTime();
				const balance = mortgageHistory.filter((p) => {
					const t = new Date(p.x).getTime();
					return t >= first && t <= last;
				});
				// A mixed chart needs each series' type; the plain chart keeps none, as before.
				if (balance.length >= 2) {
					series[0].type = info.agent.valuationChartType === "BAR" ? "column" : "area";
					series.push({ name: "Mortgage balance (estimated)", type: "line", data: balance });
				}
			}
			return series;
		}
		return [];
	}

	return (
		<Card sx={{ flexGrow: 1 }}>
			<Grid2 size={{ xs: 12 }} textAlign={"center"} p={1}>
				<Grid2 size={{ xs: 12 }} textAlign={"left"}>
					<Typography variant="h6" sx={{ fontWeight: 500, fontSize: "20px", lineHeight: "28px" }}>
						Home Value Trend
					</Typography>
				</Grid2>
				<Grid2 size={{ xs: 12 }} textAlign={"right"}>
					<ButtonGroup variant="outlined" aria-label="Basic button group">
						<Button
							sx={{
								fontSize: "14px",
								textTransform: "capitalize",
								fontWeight: 500,
								color: "black",
								backgroundColor:
									selectedChartView === ChartView.SIX_MONTHS ? selectedColor : unselectedColor,
								borderColor: appState.colorPallete["scheme3"],
							}}
							onClick={() => setSelectedChartView(ChartView.SIX_MONTHS)}
							size="small"
						>
							6 Months
						</Button>
						<Button
							sx={{
								fontSize: "14px",
								textTransform: "capitalize",
								fontWeight: 500,
								color: "black",
								backgroundColor:
									selectedChartView === ChartView.ONE_YEAR ? selectedColor : unselectedColor,
								borderColor: appState.colorPallete["scheme3"],
							}}
							onClick={() => setSelectedChartView(ChartView.ONE_YEAR)}
							size="small"
						>
							1 Year
						</Button>
						<Button
							sx={{
								fontSize: "14px",
								textTransform: "capitalize",
								fontWeight: 500,
								color: "black",
								backgroundColor: selectedChartView === ChartView.MAX ? selectedColor : unselectedColor,
								borderColor: appState.colorPallete["scheme3"],
							}}
							onClick={() => setSelectedChartView(ChartView.MAX)}
							size="small"
						>
							Max
						</Button>
					</ButtonGroup>
					{info.agent.valuationChartType === "AREA" && (
						<ReactApexChart
							key={withMortgage ? "area-m" : "area"}
							series={seriesArea}
							options={optionArea}
							type={withMortgage ? "line" : "area"}
							height={406}
						/>
					)}
					{info.agent.valuationChartType === "BAR" && (
						<ReactApexChart
							key={withMortgage ? "bar-m" : "bar"}
							series={seriesArea}
							options={optionArea}
							type={withMortgage ? "line" : "bar"}
							height={406}
						/>
					)}
				</Grid2>
			</Grid2>
			<Typography
				variant="body1"
				sx={{ backgroundColor: appState.colorPallete["scheme2"], m: 2, borderRadius: "5px" }}
			>
				<InfoIcon fontSize="inherit" /> &nbsp;
				<label>{disclaimer}</label>
			</Typography>
		</Card>
	);
}
